import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LockFill, Plus } from '@peacecover/icon';
import { useAdMob } from '../hooks/useAdMob';
import { useSuccessPhotoCardToast } from '../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import { ReactComponent as LoadingIcon } from '../../../icons/streamline-ultimate_loading.svg';
import type { components } from '../../../types/schema';
import { FEATURE_KEYS } from '../../../domains/features/constants';
import { useFeatureEnabled } from '../../../domains/features/ui/hooks/useFeatureEnabled';

type ChatRoomUserPicture = components['schemas']['ApiV1BettieChatRoomUserPicture'] & {
  nickname?: string;
};

interface HorizontalProfileSectionProps {
  bettieChatRoomPictures: ChatRoomUserPicture[];
  hasRecommendPictures: boolean;
  recommendPreviewPictureUrl?: string | null;
  onClickAddProfileImage: () => void;
  onSwitchProfilePicture: (picture: ChatRoomUserPicture) => void;
  onBeforeRecommendNewProfile: () => boolean;
  onAfterRecommendNewProfile: () => void;
  horizontalScrollResetKey?: number;
}

const HorizontalProfileSection = ({
  bettieChatRoomPictures,
  hasRecommendPictures,
  recommendPreviewPictureUrl,
  onClickAddProfileImage,
  onSwitchProfilePicture,
  onBeforeRecommendNewProfile,
  onAfterRecommendNewProfile,
  horizontalScrollResetKey
}: HorizontalProfileSectionProps) => {
  const isAdRequestInFlightRef = useRef(false);
  const onAfterRecommendNewProfileRef = useRef(onAfterRecommendNewProfile);
  const showSuccessToast = useSuccessPhotoCardToast();
  const { t } = useTranslation();

  const { data: isAdminFeatureEnabled } = useFeatureEnabled(FEATURE_KEYS.ADMIN);

  useEffect(() => {
    onAfterRecommendNewProfileRef.current = onAfterRecommendNewProfile;
  }, [onAfterRecommendNewProfile]);

  const { showAd, isLoading: isAdLoading } = useAdMob({
    adType: 'interstitial',
    featureKind: 'interstitial',
    autoLoad: true,
    onAdClosed: () => {
      onAfterRecommendNewProfileRef.current();
      isAdRequestInFlightRef.current = false;
    },
    onError: () => {
      isAdRequestInFlightRef.current = false;
    }
  });

  const handleClickRecommendNewProfile = useCallback(() => {
    const canRecommend = onBeforeRecommendNewProfile();
    if (!canRecommend) return;

    if (isAdRequestInFlightRef.current)
      // 중복 방지: 연타/재진입 시 한 번만 요청
      return;

    if (isAdminFeatureEnabled?.featureFlag?.enabled ?? false) {
      // Admin은 광고 없이 바로 추천 프로필 요청
      isAdRequestInFlightRef.current = true;
      try {
        onAfterRecommendNewProfileRef.current();
      } finally {
        isAdRequestInFlightRef.current = false;
      }
      return;
    }

    if (isAdLoading)
      return showSuccessToast(
        'bottom',
        t('BettieFreeCharge.freeChargeEnergy.loadAd') as string,
        false,
        <LoadingIcon width={20} height={20} />
      );

    isAdRequestInFlightRef.current = true;
    showAd();
  }, [
    onBeforeRecommendNewProfile,
    isAdminFeatureEnabled,
    showAd,
    showSuccessToast,
    isAdLoading,
    t
  ]);

  return (
    <HorizontalScroll
      key={horizontalScrollResetKey}
      className="w-full"
      listClassName="flex items-center gap-[20px] min-w-max px-[calc(50%-46px)]"
    >
      {bettieChatRoomPictures
        .filter((picture) => Boolean(picture.pictureUrl))
        .map((picture) => (
          <MyBackUpProfileButton
            key={picture.pictureUrl}
            picture={picture}
            onSwitchProfilePicture={onSwitchProfilePicture}
          />
        ))}
      {hasRecommendPictures && (
        <RecommendNewProfileButton
          handleClickRecommendNewProfile={handleClickRecommendNewProfile}
          recommendPreviewPictureUrl={recommendPreviewPictureUrl}
        />
      )}
      <button
        type="button"
        className="w-[92px] h-[92px] bg-secondary-light dark:bg-secondary-dark rounded-full flex items-center justify-center"
        onClick={onClickAddProfileImage}
      >
        <Plus size={38} className="text-on-secondary" />
      </button>
    </HorizontalScroll>
  );
};

export default HorizontalProfileSection;

const MyBackUpProfileButton = ({
  picture,
  onSwitchProfilePicture
}: {
  picture: ChatRoomUserPicture;
  onSwitchProfilePicture: (picture: ChatRoomUserPicture) => void;
}) => {
  const isVisible = picture.visibility ?? true;
  return (
    <button type="button" onClick={() => onSwitchProfilePicture(picture)}>
      <div className="relative">
        <img
          src={picture.pictureUrl}
          alt={picture.pictureUrl}
          className="w-[92px] h-[92px] object-cover rounded-full"
        />
        {!isVisible && (
          <div className="absolute bottom-[4px] right-[4px] p-[4px] bg-disable rounded-full border border-background-elevated">
            <LockFill size={12} className="text-text-main-lv2" />
          </div>
        )}
      </div>
    </button>
  );
};

const RecommendNewProfileButton = ({
  handleClickRecommendNewProfile,
  recommendPreviewPictureUrl
}: {
  handleClickRecommendNewProfile: () => void;
  recommendPreviewPictureUrl: string | null | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <button type="button" className="relative" onClick={handleClickRecommendNewProfile}>
      <img
        src="/assets/icons/profile_bubble.png"
        alt="recommend new profile"
        className="w-[92px] h-[92px] object-cover absolute inset-0"
      />
      {recommendPreviewPictureUrl && (
        <img
          src={recommendPreviewPictureUrl}
          alt="recommend preview"
          className="w-[92px] h-[92px] object-cover rounded-full"
        />
      )}
      <p className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] text-[13px] font-semibold leading-[16px] text-pure-white text-center whitespace-pre-line">
        {t('BettieChangeProfile.getRecommendPicture')}
      </p>
    </button>
  );
};
